import { ChevronLeftIcon, ChevronRightIcon } from '@c/icons';
import SafeImage from '@ui/SafeImage';
import ZoomableImage from '@ui/ZoomableImage';
import { useEffect, useState, useRef, useCallback } from 'react';
import BaseModal from './BaseModal';
import { ProductDocument } from 'models/product';
import { Montserrat } from 'next/font/google';

const montserrat = Montserrat({
  subsets: ['latin'],
  weight: ['400', '500', '600', '700'],
});
interface ProductViewerModalProps {
  isOpen: boolean;
  dismiss: () => void;
  images?: ProductDocument['images'];
  video?: ProductDocument['video'];
  initialImage?: number;
}
const ProductViewerModal = ({
  isOpen,
  dismiss,
  images,
  video,
  initialImage = 0,
}: ProductViewerModalProps) => {
  const [currentImage, setCurrentImage] = useState(initialImage);
  const [isVerticalImage, setIsVerticalImage] = useState(false);
  const touchStartRef = useRef<{ x: number; y: number; time: number } | null>(
    null
  );
  const touchMoveRef = useRef<{ x: number; y: number } | null>(null);
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const SWIPE_THRESHOLD = 50; // Minimum distance for swipe
  const SWIPE_VELOCITY_THRESHOLD = 0.3; // Minimum velocity for swipe
  const MAX_VERTICAL_SWIPE = 30; // Max vertical movement to consider it horizontal swipe

  useEffect(() => {
    if (isOpen) {
      setCurrentImage(initialImage);
      setIsVerticalImage(false); // Reset orientation when modal opens
    }
  }, [initialImage, isOpen]);

  useEffect(() => {
    // Reset orientation when image changes
    setIsVerticalImage(false);
  }, [currentImage]);

  const handleImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
      const img = e.currentTarget as HTMLImageElement;
      if (img && img.naturalWidth && img.naturalHeight) {
        const aspectRatio = img.naturalHeight / img.naturalWidth;
        setIsVerticalImage(aspectRatio > 1);
      }
    },
    []
  );

  const goToPrevious = useCallback(() => {
    if (currentImage > 0) {
      setCurrentImage(currentImage - 1);
    } else if (video && currentImage !== -1) {
      setCurrentImage(-1);
    } else if (images) {
      setCurrentImage(images.length - 1);
    }
  }, [currentImage, images, video]);

  const goToNext = useCallback(() => {
    if (images && currentImage < images.length - 1) {
      setCurrentImage(currentImage + 1);
    } else if (video) {
      setCurrentImage(-1);
    } else if (images) {
      setCurrentImage(0);
    }
  }, [currentImage, images, video]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      const touch = e.touches[0];
      touchStartRef.current = {
        x: touch.clientX,
        y: touch.clientY,
        time: Date.now(),
      };
      touchMoveRef.current = null;
    }
    // Allow event to propagate for swipe detection
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 1 && touchStartRef.current) {
      const touch = e.touches[0];
      touchMoveRef.current = {
        x: touch.clientX,
        y: touch.clientY,
      };
    }
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (!touchStartRef.current || !touchMoveRef.current) {
        touchStartRef.current = null;
        touchMoveRef.current = null;
        return;
      }

      const deltaX = touchMoveRef.current.x - touchStartRef.current.x;
      const deltaY = touchMoveRef.current.y - touchStartRef.current.y;
      const deltaTime = Date.now() - touchStartRef.current.time;
      const distance = Math.abs(deltaX);
      const verticalDistance = Math.abs(deltaY);
      const velocity = distance / deltaTime;

      // Only trigger swipe if:
      // 1. Horizontal movement is greater than vertical (horizontal swipe)
      // 2. Distance is above threshold
      // 3. Velocity is above threshold OR distance is significant
      // Reduced threshold for easier swiping
      if (
        distance > verticalDistance &&
        distance > SWIPE_THRESHOLD * 0.5 && // Reduced threshold for easier swiping
        verticalDistance < MAX_VERTICAL_SWIPE &&
        (velocity > SWIPE_VELOCITY_THRESHOLD * 0.5 ||
          distance > SWIPE_THRESHOLD) // More lenient velocity check
      ) {
        if (deltaX > 0) {
          // Swipe right - go to previous
          goToPrevious();
        } else {
          // Swipe left - go to next
          goToNext();
        }
      }

      touchStartRef.current = null;
      touchMoveRef.current = null;
    },
    [goToPrevious, goToNext]
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!images) return;
      if (e.key === 'ArrowLeft') {
        if (currentImage > 0) {
          setCurrentImage(currentImage - 1);
        } else {
          setCurrentImage(images.length - 1);
        }
      } else if (e.key === 'ArrowRight') {
        if (currentImage < images.length - 1) {
          setCurrentImage(currentImage + 1);
        } else {
          setCurrentImage(0);
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [currentImage, images]);

  if (!images && !video) return null;

  const full = currentImage >= 0 ? images?.[currentImage]?.full : undefined;
  const thumb = currentImage >= 0 ? images?.[currentImage]?.thumb : undefined;
  const download_url =
    currentImage >= 0 ? images?.[currentImage]?.download_url : undefined;

  // Calculate total items (images + video if exists)
  const totalItems = (images?.length || 0) + (video ? 1 : 0);
  // Calculate current item number
  // If showing video (currentImage === -1), it's always item 1
  // If showing an image and video exists, images start at 2 (video is 1)
  // If showing an image and no video, images start at 1
  const currentItemNumber =
    currentImage === -1 ? 1 : video ? currentImage + 2 : currentImage + 1;

  return (
    <BaseModal
      isOpen={isOpen}
      dismiss={dismiss}
      isFullScreen
      className="!z-[70]"
      title={`Gallery (${currentItemNumber} of ${totalItems})`}
    >
      <div className="flex h-full w-full flex-col overflow-hidden bg-white">
        {/* Image Container */}
        <div
          ref={imageContainerRef}
          className="relative flex flex-1 touch-none items-center justify-center overflow-hidden bg-white"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Previous button */}
          <button
            onClick={goToPrevious}
            className={`absolute z-20 flex h-16 w-16 items-center justify-center rounded-full border border-gray-200 bg-white/90 text-black shadow-lg transition-opacity hover:border-gray-300 hover:bg-white active:opacity-80 ${
              isVerticalImage ? 'left-2 md:left-4' : 'left-4 md:left-6'
            }`}
            aria-label="Previous image"
          >
            <ChevronLeftIcon width={20} height={20} />
          </button>

          {/* Image */}
          <div className="relative h-full w-full overflow-hidden">
            {currentImage === -1 && video ? (
              <video
                autoPlay
                loop
                muted
                playsInline
                draggable={false}
                src={video.full}
                className="mx-auto h-full w-full object-contain"
              ></video>
            ) : full ? (
              <ZoomableImage
                key={full}
                src={full}
                fallbackSrc={download_url}
                blurDataURL={thumb}
                alt="current image"
                onImageChange={() => {
                  // Reset zoom when image changes
                }}
                className="h-full w-full"
                onLoad={handleImageLoad}
              />
            ) : null}
          </div>

          {/* Next button */}
          <button
            onClick={goToNext}
            className={`absolute z-20 flex h-16 w-16 items-center justify-center rounded-full border border-gray-200 bg-white/90 text-black shadow-lg transition-opacity hover:border-gray-300 hover:bg-white active:opacity-80 ${
              isVerticalImage ? 'right-2 md:right-4' : 'right-4 md:right-6'
            }`}
            aria-label="Next image"
          >
            <ChevronRightIcon width={20} height={20} />
          </button>
        </div>

        {/* Thumbnail Strip */}
        <div className="border-t border-gray-200 bg-white px-4 py-3">
          <div className="no-scrollbar flex justify-center gap-2 overflow-x-auto pb-1">
            {video && (
              <button
                type="button"
                onClick={() => setCurrentImage(-1)}
                onMouseEnter={() => setCurrentImage(-1)}
                className={`relative h-14 w-14 flex-shrink-0 overflow-hidden rounded-md border-2 transition-all ${
                  currentImage === -1
                    ? 'scale-105 border-black opacity-100'
                    : 'border-transparent opacity-60 active:opacity-100'
                }`}
              >
                <video
                  loop
                  muted
                  playsInline
                  draggable={false}
                  src={video.full}
                  className="h-full w-full object-cover"
                ></video>
                <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="text-white"
                  >
                    <path d="M8 5v14l11-7z" fill="currentColor" />
                  </svg>
                </div>
              </button>
            )}

            {images?.map((image, index) => (
              <button
                key={image.thumb + `${index}`}
                type="button"
                onClick={() => setCurrentImage(index)}
                onMouseEnter={() => setCurrentImage(index)}
                className={`relative h-14 w-14 flex-shrink-0 overflow-hidden rounded-md border-2 transition-all ${
                  index === currentImage
                    ? 'scale-105 border-black opacity-100'
                    : 'border-transparent opacity-60 active:opacity-100'
                }`}
              >
                <SafeImage
                  fill
                  draggable={false}
                  src={image.thumb}
                  fallbackSrc={image.download_url}
                  alt={`image ${index + 1}`}
                  className="object-cover"
                  skipOptimize
                />
              </button>
            ))}
          </div>
        </div>
      </div>
    </BaseModal>
  );
};

export default ProductViewerModal;
